/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// redux utils
import { createSlice } from '@reduxjs/toolkit';
// thunks
import {
  getProductCodeUploadStatus,
  getProductManagerConfig,
  updateControlBarConfig,
  updateProductManagerConfig
} from 'state/thunks/productmanager'
import { isObjectArrayEqual } from 'utils/isObjectArrayEqual';
import { isProductGroupDraftLiveEqual } from 'utils/productgroup';
/* ========== ~~~~~~~~~~ PRODUCT MANAGER SLICE : REDUCER ~~~~~~~~~~ ========== */

const initialProductManagerConfig = {
  id: null,
  commentsCounterEnabled: true,
  groupConfigList: [],
  removedGroupIds: []
}

const initialState = {
  status: 'initial',
  codeUploadStatus: 'INITIAL',
  tempProductManagerConfig: {
    id: null,
    commentsCounterEnabled: false,
    controlBarConfig: null,
    groupConfigList: [],
    removedGroupIds: []
  },
  productManagerConfig: {
    id: null,
    commentsCounterEnabled: false,
    controlBarConfig: null,
    groupConfigList: [],
    removedGroupIds: []
  },
  // productManagerConfigChanges: {
  //   pageSetup: [],
  //   codeIntegration: []
  // }
  productManagerConfigChanges: []
};


export const productManagerSlice = createSlice({
    name: 'productManager',
    initialState,
    reducers: {
        setTempProductManagerConfig: (state, action) => {
            const { config, changes } = action.payload
            state.tempProductManagerConfig = config
            state.productManagerConfigChanges.push(changes)

            // // use for displaying the changes when user click publish and save draft/current
            // if(state.tempProductManagerConfig.commentsCounterEnabled !== state.productManagerConfig.commentsCounterEnabled) {
            //   state.productManagerConfigChanges.codeIntegration = ['Additional - Comments Counter']
            // } else {
            //   state.productManagerConfigChanges.codeIntegration = []
            // }

            // // use for displaying changes on page setup
            // let pageSetupChanges = []
            // state.tempProductManagerConfig.groupConfigList.forEach((group, index) => {
            //   const savedGroup = state.productManagerConfig.groupConfigList[index]
            //   const isGroupChanged = !(JSON.stringify(group) === JSON.stringify(savedGroup || null));
            //   if(isGroupChanged) {
            //     const isDraftChanged = !(JSON.stringify(group.draft) === JSON.stringify(savedGroup?.draft || null));
            //     const isCurrentChanged = !(JSON.stringify(group.current) === JSON.stringify(savedGroup?.current || null));
            //     pageSetupChanges = [
            //       ...pageSetupChanges,
            //       `Group ${index + 1} - ${isDraftChanged ? group.draft.label : ''} ${isCurrentChanged ? `${isDraftChanged ? '/' : ''} ${group.current.label}` : ''}`
            //     ]
            //   }
            // })

            // state.productManagerConfigChanges.pageSetup = pageSetupChanges
        },
        revertProductManagerConfigChanges: (state, action) => {
          state.tempProductManagerConfig = state.productManagerConfig
          // Use to persist the group config tested state
          if(state.productManagerConfigChanges.includes('groupConfigTested')) state.productManagerConfigChanges = ['groupConfigTested']
          else state.productManagerConfigChanges = []
          // state.productManagerConfigChanges = {
          //   pageSetup: [],
          //   codeIntegration: []
          // }
        },
        revertDraftToLive: (state, action) => {
          // revert draft control bar config to live
          if(state.tempProductManagerConfig.controlBarConfig.draft && state.tempProductManagerConfig.controlBarConfig.current) {
            if(state.tempProductManagerConfig.controlBarConfig.current.desktop)
              state.tempProductManagerConfig.controlBarConfig.draft = state.tempProductManagerConfig.controlBarConfig.current
          }
          
          // revert draft group configs to live
          state.tempProductManagerConfig.groupConfigList.forEach((group, index) => {
            const isGroupStatusPending = state.tempProductManagerConfig.groupConfigList[index].current && state.tempProductManagerConfig.groupConfigList[index].current.label === null
            if(!isGroupStatusPending) 
              state.tempProductManagerConfig.groupConfigList[index].draft = state.tempProductManagerConfig.groupConfigList[index].current
          })
          if(state.tempProductManagerConfig.groupConfigList.length === 0) {
            state.tempProductManagerConfig.groupConfigList = state.productManagerConfig.groupConfigList
            state.tempProductManagerConfig.groupConfigList.forEach((group, index) => {
              const isGroupStatusPending = state.tempProductManagerConfig.groupConfigList[index].current && state.tempProductManagerConfig.groupConfigList[index].current.label === null
              if(!isGroupStatusPending) 
                state.tempProductManagerConfig.groupConfigList[index].draft = state.tempProductManagerConfig.groupConfigList[index].current
            })
          }
        }
    },
    extraReducers: builder => {
      builder
        // get product manager config
        .addCase(getProductManagerConfig.pending, (state, action) => {
            state.productManagerConfig = {...initialState.productManagerConfig, status: 'pending'}
            state.tempProductManagerConfig = state.productManagerConfig
        })
        .addCase(getProductManagerConfig.fulfilled, (state, action) => {
            const populateWithDefaultGroupConfigValues = (group) => {
              const populateGroupState = (groupState) => {
                if (groupState.layout === null) groupState.layout = 'INLINE'
                if (groupState.trigger === null) groupState.trigger = 'NONE'
                if (groupState.deviceType === null) groupState.deviceType = 'ALL'
              }
              populateGroupState(group.draft)
              populateGroupState(group.current)
            }

            action.payload.groupConfigList.forEach(populateWithDefaultGroupConfigValues)

            state.productManagerConfig = {...action.payload, status: 'fulfilled'}
            // when there's no group list yet - use a default one 
            if(!state.productManagerConfig.groupConfigList.length) 
              state.productManagerConfig.groupConfigList = initialProductManagerConfig.groupConfigList
            state.tempProductManagerConfig = state.productManagerConfig

            state.tempProductManagerConfig.groupConfigList.forEach((group) => {
              if(isProductGroupDraftLiveEqual(group)) return
              state.productManagerConfigChanges = ['groupConfigTested']
            })
        })
        .addCase(getProductManagerConfig.rejected, (state, action) => {
            state.productManagerConfig = {...initialState.productManagerConfig, status: 'rejected'}
            // when there's no group list yet - use a default one 
            if(!state.productManagerConfig.groupConfigList.length) 
              state.productManagerConfig.groupConfigList = initialProductManagerConfig.groupConfigList
            state.tempProductManagerConfig = state.productManagerConfig
        })   


        // update product manager config
        .addCase(updateProductManagerConfig.pending, (state, action) => {
            state.productManagerConfig.isUpdating = true
        })
        .addCase(updateProductManagerConfig.fulfilled, (state, action) => {

            if(action.payload) {
                state.productManagerConfig = action.payload.productManager
                state.tempProductManagerConfig = action.payload.productManager

                if(action.payload.controlBar) {
                  state.productManagerConfig.controlBarConfig.draft = action.payload.controlBar
                  state.tempProductManagerConfig.controlBarConfig.draft = action.payload.controlBar
                }
            }
            state.productManagerConfigChanges = []
            state.tempProductManagerConfig.groupConfigList.forEach((group) => {
              if(isProductGroupDraftLiveEqual(group)) return
              state.productManagerConfigChanges = ['groupConfigTested']
            })
            state.tempProductManagerConfig.isUpdating = false
        })
        .addCase(updateProductManagerConfig.rejected, (state, action) => {
            state.tempProductManagerConfig.isUpdating = false
        })    
  

        // update control bar config
        .addCase(updateControlBarConfig.pending, (state, action) => {
        })
        .addCase(updateControlBarConfig.fulfilled, (state, action) => {
          if(state.tempProductManagerConfig.controlBarConfig) {
            state.tempProductManagerConfig.controlBarConfig = {
              ...state.tempProductManagerConfig.controlBarConfig,
              draft: action.payload
            }
          } else {
            state.tempProductManagerConfig.controlBarConfig = {
              draft: action.payload
            }
          }
        })
        .addCase(updateControlBarConfig.rejected, (state, action) => {
        })    

        // get product code upload status
        .addCase(getProductCodeUploadStatus.pending, (state, action) => {
          state.codeUploadStatus = 'IN_PROGRESS'
        })
        .addCase(getProductCodeUploadStatus.fulfilled, (state, action) => {
          state.codeUploadStatus = action.payload
        })
        .addCase(getProductCodeUploadStatus.rejected, (state, action) => {
          state.codeUploadStatus = 'FAILED'

        })   
    }
});

export const { setTempProductManagerConfig, revertProductManagerConfigChanges, revertDraftToLive } = productManagerSlice.actions
export default productManagerSlice.reducer;
